import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Input,
  Paper,
  Typography
} from "@mui/material";
import Tooltip from "../../../Atoms/Tooltip";
import moment from 'moment-timezone';
import { useEffect, useMemo, useState } from 'react';
import { useCEPData } from '../../../../Hooks';
import useCallAxios from '../../../../Hooks/useCallAxios';
import { formatInputValue } from '../../../../Utils/formatInputValue';
import { cepMask, handleTaxIDInput } from '../../../../Utils/masks';

moment.tz.setDefault('America/Sao_Paulo');

interface ServiceInvoiceDetail {
  file: any;
  onDelete: (fileId: string) => void;
  onUpdate: (fileId: string, data: any) => void;
}

export const ServiceInvoiceDetail: React.FC<ServiceInvoiceDetail> = ({ file, onDelete, onUpdate }) => {
  const [document, setDocument] = useState<string | undefined>();
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const { requestData: cepData, fetchCEPData, requestLoading: requestLoadingCEP } = useCEPData();

  const context = "invoiceBuyers";

  const {
    fetchGET: fetchInvoiceBuyer,
    requestLoading: requestLoading,
    requestData,
  } = useCallAxios({ context });

  const [localIssueDate, setLocalIssueDate] = useState(file.issueDate ? moment(file.issueDate).format('YYYY-MM-DDTHH:mm') : moment().format('YYYY-MM-DDTHH:mm'));

  useEffect(() => {
    if (file.issueDate) {
      setLocalIssueDate(moment(file.issueDate).format('YYYY-MM-DDTHH:mm'));
    }
  }, [file.issueDate]);

  // Initialize defaults into parent state to avoid UI-only fallbacks not being submitted
  useEffect(() => {
    const updates: any = {};
    if (!file.vOrig) updates.vOrig = formatInputValue('0');
    if (!file.vTax) updates.vTax = formatInputValue('0');
    if (!file.issueDate) updates.issueDate = moment().format('YYYY-MM-DDTHH:mm');

    if (!file.dup || file.dup.length === 0) {
      updates.dup = [{ vDup: 0, vDupString: formatInputValue('0'), dVenc: moment().add(1, 'days').format('YYYY-MM-DD') }];
    }

    if (Object.keys(updates).length > 0) {
      onUpdate(file.id, updates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const docDigits = (document || '').replace(/\D/g, '');
    if (!docDigits || docDigits.length < 11) return;

    fetchInvoiceBuyer({
      queryParameters: {
        pageSize: 1,
        page: 1,
        document: docDigits,
      },
    });
  }, [document]);

  useEffect(() => {
    if (!requestData || (requestData as any)?.status !== 200) return;

    if ((requestData as any)?.data?.customers?.length > 0) {
      const customer = (requestData as any)?.data?.customers[0];
      onUpdate(file.id, {
        customer: {
          ...file.customer,
          name: customer.trade_Name,
          email: customer.email,
          address: customer.address,
          number: customer.number,
          complement: customer.complement,
          district: customer.district,
          zipCode: cepMask(customer.postal_Code),
          ie: customer.ie,
          city: customer.city,
          state: customer.state,
        },
      });
    } 
    else {
      onUpdate(file.id, {
        customer: {
          ...file.customer,
          name: '',
          email: '',
          address: '',
          number: '',
          complement: '',
          district: '',
          zipCode: '',
          city: '',
          state: '',
        },
      });
    }
  }, [requestData]);

  useEffect(() => {
    if (!cepData) return;

    const cepResponse = cepData as any;

    if (cepResponse?.erro) {
      onUpdate(file.id, {
        customer: {
          ...file.customer,
          address: '',
          district: '',
          state: '',
          city: ''
        }
      });
      return;
    }

    const updatedCustomer = {
      ...file.customer,
      number: '',
      address: cepResponse.logradouro || file.customer?.address || '',
      district: cepResponse.bairro || file.customer?.district || '',
      state: cepResponse.uf || file.customer?.state || '',
      city: cepResponse.localidade || file.customer?.city || ''
    };

    onUpdate(file.id, { customer: updatedCustomer });
  }, [cepData, file.id]);

  const updateField = (field, value) => {
    onUpdate(file.id, { [field]: value });
  };
  const liquidValue = useMemo(() => {
    const formatBrCurrencyToNumber = (value) => {
      if (!value) return 0;
      return parseFloat(value.toString().replace(/\./g, '').replace(',', '.'));
    };

    const total = formatBrCurrencyToNumber(file.vOrig);
    const tax = formatBrCurrencyToNumber(file.vTax);
    const liquid = Math.max(0, total - tax);

    return liquid.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }, [file.vOrig, file.vTax]);

  const updateDuplicate = (index, key, value) => {
    const newDups = [...(file.dup || [])];
    newDups[index][key] = value;
    onUpdate(file.id, { dup: newDups });
  };

  const updateCustomer = (field, value) => {
    const currentCustomer = file.customer || {};
    const updatedCustomer = {
      ...currentCustomer,
      [field]: value
    };
    onUpdate(file.id, { customer: updatedCustomer });
  };

  const handleIssueDateChange = (value) => {
    setLocalIssueDate(value);
    updateField('issueDate', value);
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        mt: 3,
        mb: 3
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} container spacing={2}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="caption">Arquivo</Typography>
            <Tooltip title="Remover nota">
              <IconButton
                size="small"
                onClick={() => onDelete(file.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            </Box>
            <Tooltip title={file.fileName || 'NFS-e.pdf'}>
              <Typography
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>{file.fileName || 'NFS-e.pdf'}</Typography>
            </Tooltip>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box display="flex" flexDirection="column">
              <Typography variant="caption">Código de Verificação/Autenticidade
                <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Input
                sx={{ width: '100%' }}
                value={file.verificationCode || ''}
                type="text"
                name="verificationCode"
                placeholder="Código de Verificação"
                onChange={(e) => updateField('verificationCode', e.target.value)}
                onBlur={() => setTouchedFields(prev => ({ ...prev, verificationCode: true }))}
                required
                error={touchedFields.verificationCode && !file.verificationCode}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box display="flex" flexDirection="column">
              <Typography variant="caption" sx={{ xs: 12 }}>Número da Nota
                <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Input
                value={file.number || ''}
                type="number"
                name="number"
                placeholder="Número da Nota"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || parseInt(value) >= 0) {
                    updateField('number', value);
                  }
                }}
                onBlur={() => {
                  setTouchedFields(prev => ({ ...prev, number: true }));
                  if (file.number === '' || parseInt(file.number) <= 0) {
                    updateField('number', '');
                  }
                }}
                required
                inputProps={{ min: 1 }}
                error={touchedFields.number && (!file.number || parseInt(file.number) <= 0)}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box display="flex" flexDirection="column">
            <Typography variant="caption">Emissão</Typography>
            <Input
              type="datetime-local"
              name="issueDate"
              value={localIssueDate}
              onChange={(e) => handleIssueDateChange(e.target.value)}
              onBlur={(e) => {
                const selectedDate = moment(e.target.value);
                const now = moment();

                if (selectedDate.isAfter(now)) {
                  const nowFormatted = now.format('YYYY-MM-DDTHH:mm');
                  setLocalIssueDate(nowFormatted);
                  onUpdate(file.id, { issueDate: nowFormatted });
                }
              }}
              inputProps={{ max: moment().format('YYYY-MM-DDTHH:mm') }}
            />
          </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box display="flex" flexDirection="column">
              <Box display="flex" alignItems="center">
                <Typography variant="caption" sx={{ xs: 12 }}>Impostos Retidos</Typography>
                <Tooltip title="Refere-se aos impostos retidos como PIS, IR, INSS, entre outros.">
                  <IconButton size="small" sx={{ padding: '0px', marginLeft: '2px', marginTop: '-5px' }}>
                    <HelpOutlineIcon sx={{ fontSize: '10px' }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Input
                value={file.vTax ?? ''}
                onChange={(e) => {
                  updateField('vTax', formatInputValue(e.target.value))
                }}
                name="vTax"
                type="text"
                placeholder="Valor (R$)"
                sx={{ color: 'black' }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Box display="flex" flexDirection="column">
              <Typography variant="caption" sx={{ xs: 12 }}>Valor Total
                <span style={{ color: 'red' }}>*</span></Typography>
              <Input
                value={file.vOrig ?? ''}
                onChange={(e) => {
                  updateField('vOrig', formatInputValue(e.target.value))
                }}
                onBlur={() => setTouchedFields(prev => ({ ...prev, vOrig: true }))}
                required
                error={touchedFields.vOrig && !file.vOrig}
                name="vOrig"
                type="text"
                placeholder="Valor (R$)"
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Box display="flex" flexDirection="column">
              <Typography variant="caption">Valor Liquido</Typography>
              <Typography>{liquidValue}</Typography>
            </Box>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} mt={2}>
            <Typography variant="h5">Duplicatas</Typography>
            <IconButton
              size="small"
              onClick={() => {
                const newDups = [...(file.dup || [])];
                newDups.push({ vDup: 0, dVenc: '' });
                onUpdate(file.id, { dup: newDups });
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box display="flex" flexDirection="column" gap={1}>
            {(!file.dup || file.dup.length === 0) ? (
              <Box>Carregando duplicata...</Box>
            ) : (
              file.dup.map((dup, index) => (
                <Box key={index} display="flex" gap={2} alignItems="center">
                  <Box display="flex" flexDirection="column">
                    <Typography variant="caption" sx={{ xs: 12 }}>Valor da Duplicata
                      <span style={{ color: 'red' }}>*</span></Typography>
                    <Input
                      value={dup.vDupString ?? ''}
                      onChange={(e) => {
                        const formattedValue = formatInputValue(e.target.value);
                        const decimalValue = parseFloat(formattedValue.replace(/\./g, '').replace(',', '.'));
                        updateDuplicate(index, 'vDupString', formattedValue);
                        updateDuplicate(index, 'vDup', decimalValue);
                        if (!dup.dVenc) updateDuplicate(index, 'dVenc', moment().add(1, 'days').format('YYYY-MM-DD'));
                      }}
                      name="vDupString"
                      type="text"
                      placeholder="Valor (R$)"
                    />
                  </Box>
                  <Box display="flex" flexDirection="column">
                    <Typography variant="caption" sx={{ xs: 12 }}>Data Vencimento
                      <span style={{ color: 'red' }}>*</span></Typography>
                    <Input
                      type="date"
                      name="dVenc"
                      value={dup.dVenc}
                      onChange={(e) => updateDuplicate(index, 'dVenc', e.target.value)}
                      fullWidth
                      onBlur={(e) => {
                        if(!e.target.value) return;

                        const selectedDate = moment(e.target.value);
                        const now = moment().add(1, 'days');

                        if (selectedDate.isBefore(now)) {
                          updateDuplicate(index, 'dVenc', now.format('YYYY-MM-DD'));
                        }
                      }}
                      inputProps={{ min: moment().add(1, 'days').format('YYYY-MM-DD') }}
                    />
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newDups = [...file.dup];
                      newDups.splice(index, 1);
                      onUpdate(file.id, { dup: newDups });
                    }}
                    disabled={file.dup.length === 1}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))
            )}
          </Box>
        </Grid>

        <Grid item xs={12} sx={{ position: 'relative' }}>
          {requestLoading?.GET || requestLoadingCEP && (
            <Box
              position="absolute"
              display="flex"
              justifyContent="center"
              alignItems="center"
              top={0}
              left={0}
              right={0}
              bottom={0}
              zIndex={1}
            >
              <CircularProgress size={24} />
            </Box>
          )}
          <Box
            sx={{ opacity: requestLoading?.GET || requestLoadingCEP ? 0.6 : 1 }}>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
              mt={2}
            >
              <Typography variant="h5">Tomador do Serviço</Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Box display="flex" flexDirection="column">
                  <Typography variant="caption" sx={{ xs: 12, md: 6 }}>Documento
                    <span style={{ color: 'red' }}>*</span></Typography>
                  <Input
                    value={file.customer?.document}
                    onChange={(e) => {
                      updateCustomer('document', handleTaxIDInput(e.currentTarget.value))
                    }}
                    name="document"
                    type="text"
                    placeholder="CPF ou CNPJ"
                    disabled={requestLoading?.GET}
                    onBlur={(e) => {
                      setDocument(e.currentTarget.value.replace(/\D/g, ''))
                      setTouchedFields(prev => ({ ...prev, customerDocument: true }))
                    }}
                    required
                    error={touchedFields.customerDocument && !file.customer?.document}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={9}>
                <Box display="flex" flexDirection="column">
                  <Typography variant="caption" sx={{ xs: 12 }}>Nome
                    <span style={{ color: 'red' }}>*</span></Typography>
                  <Input
                    value={file.customer?.name || ''}
                    type="text"
                    name="customerName"
                    placeholder="Nome do Cliente"
                    onChange={(e) => updateCustomer('name', e.target.value)}
                    onBlur={() => setTouchedFields(prev => ({ ...prev, customerName: true }))}
                    required
                    error={touchedFields.customerName && !file.customer?.name}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={3}>
                <Box display="flex" flexDirection="column">
                  <Typography variant="caption" sx={{ xs: 12 }}>Inscrição Estadual</Typography>
                  <Input
                    value={file.customer?.ie || ''}
                    type="text"
                    name="customerIE"
                    placeholder="Inscrição Estadual"
                    onChange={(e) => updateCustomer('ie', e.target.value)}
                    onBlur={() => setTouchedFields(prev => ({ ...prev, customerIE: true }))}
                    required
                    error={touchedFields.customerIE && !file.customer?.ie}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={9}>
                <Box display="flex" flexDirection="column">
                  <Typography variant="caption" sx={{ xs: 12 }}>CEP
                    <span style={{ color: 'red' }}>*</span></Typography>
                  <Input
                    value={file.customer?.zipCode || ''}
                    type="text"
                    name="customerZipCode"
                    placeholder="CEP"
                    onChange={(e) => updateCustomer('zipCode', cepMask(e.target.value))}
                    sx={{ width: { lg: '15%', md: '25%', xs: '100%' } }}
                    onBlur={async () => {
                      const rawZip = (file.customer?.zipCode || '').toString();
                      const parsedCEP = rawZip.replace(/\D/g, '');
                      if (parsedCEP.length === 8) {
                        fetchCEPData({ cep: parsedCEP });
                      }
                      setTouchedFields(prev => ({ ...prev, customerZipCode: true }))
                    }}
                    required
                    error={touchedFields.customerZipCode && !file.customer?.zipCode}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={7}>
                <Box display="flex" flexDirection="column">
                  <Typography variant="caption" sx={{ xs: 12 }}>Endereço
                    <span style={{ color: 'red' }}>*</span></Typography>
                  <Input
                    value={file.customer?.address || ''}
                    type="text"
                    name="customerAddress"
                    placeholder="Endereço"
                    onChange={(e) => updateCustomer('address', e.target.value)}
                    onBlur={() => setTouchedFields(prev => ({ ...prev, customerAddress: true }))}
                    required
                    error={touchedFields.customerAddress && !file.customer?.address}
                  />
                </Box>
              </Grid>
              <Grid item xs={2} md={2}>
                <Box display="flex" flexDirection="column">
                  <Typography variant="caption" sx={{ xs: 12 }}>Número
                    <span style={{ color: 'red' }}>*</span></Typography>
                  <Input
                    value={file.customer?.number || ''}
                    type="text"
                    name="customerNumber"
                    placeholder="Número"
                    onChange={(e) => updateCustomer('number', e.target.value)}
                    onBlur={() => setTouchedFields(prev => ({ ...prev, customerNumber: true }))}
                    required
                    error={touchedFields.customerNumber && !file.customer?.number}
                  />
                </Box>
              </Grid>
              <Grid item xs={10} md={3}>
                <Box display="flex" flexDirection="column">
                  <Typography variant="caption" sx={{ xs: 12 }}>Bairro
                    <span style={{ color: 'red' }}>*</span></Typography>
                  <Input
                    value={file.customer?.district || ''}
                    type="text"
                    name="customerDistrict"
                    placeholder="Bairro"
                    onChange={(e) => updateCustomer('district', e.target.value)}
                    onBlur={() => setTouchedFields(prev => ({ ...prev, customerDistrict: true }))}
                    required
                    error={touchedFields.customerDistrict && !file.customer?.district}
                  />
                </Box>
              </Grid>
              <Grid item xs={10} md={3}>
                <Box display="flex" flexDirection="column">
                  <Typography variant="caption" sx={{ xs: 12 }}>Cidade
                    <span style={{ color: 'red' }}>*</span></Typography>
                  <Input
                    value={file.customer?.city || ''}
                    type="text"
                    name="customerCity"
                    placeholder="Cidade"
                    onChange={(e) => updateCustomer('city', e.target.value)}
                    onBlur={() => setTouchedFields(prev => ({ ...prev, customerCity: true }))}
                    required
                    error={touchedFields.customerCity && !file.customer?.city}
                  />
                </Box>
              </Grid>
              <Grid item xs={2} md={1}>
                <Box display="flex" flexDirection="column">
                  <Typography variant="caption" sx={{ xs: 12 }}>UF
                    <span style={{ color: 'red' }}>*</span></Typography>
                  <Input
                    value={file.customer?.state || ''}
                    type="text"
                    name="customerState"
                    placeholder="Estado"
                    onChange={(e) => updateCustomer('state', e.target.value)}
                    onBlur={() => setTouchedFields(prev => ({ ...prev, customerState: true }))}
                    required
                    error={touchedFields.customerState && !file.customer?.state}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Box display="flex" flexDirection="column">
                  <Typography variant="caption" sx={{ xs: 12 }}>Complemento</Typography>
                  <Input
                    value={file.customer?.complement || ''}
                    type="text"
                    name="customerComplement"
                    placeholder="Complemento do Cliente"
                    onChange={(e) => updateCustomer('complement', e.target.value)}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};
